
import React, { useEffect, useMemo } from 'react';
import { GenericScheduleProps, Schedule14ForeignPEData, ForeignPEPriorPeriodItem, IneligiblePEItem, FormErrorsStep } from '../../types';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { RadioGroup } from '../ui/RadioGroup';
import { safeParseFloat } from '../../utils/calculations';

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h4 className="text-md font-semibold text-sky-700 mt-4 mb-2 border-b pb-1">{title}</h4>
);

export const Schedule14_ForeignPE: React.FC<GenericScheduleProps<Schedule14ForeignPEData>> = ({ scheduleData, onChange, errors, t, fullFormData }) => {
  
  const handlePriorPeriodChange = (index: number, field: keyof ForeignPEPriorPeriodItem, value: string) => {
    const updatedItems = scheduleData.priorPeriods.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    onChange('priorPeriods', updatedItems);
  };

  const addPriorPeriod = () => {
    onChange('priorPeriods', [
      ...scheduleData.priorPeriods,
      { 
        id: Date.now().toString(), 
        taxPeriodIdentifier: '', 
        taxableIncomeFPE: '', 
        taxLossFPE: '', 
        electionMadeInPeriod: '', 
        lossesUtilisedAgainstMainTI: '', 
        lossesUtilisedAgainstFPEIncome: '' 
      }
    ]);
  };

  const removePriorPeriod = (id: string) => {
    onChange('priorPeriods', scheduleData.priorPeriods.filter(item => item.id !== id));
  };
  
  const handleIneligiblePEChange = (index: number, field: keyof IneligiblePEItem, value: string) => {
    const updatedItems = scheduleData.ineligiblePEs.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    onChange('ineligiblePEs', updatedItems);
  };

  const addIneligiblePE = () => {
    onChange('ineligiblePEs', [
      ...scheduleData.ineligiblePEs,
      { id: Date.now().toString(), jurisdictionName: '' }
    ]);
  };

  const removeIneligiblePE = (id: string) => {
    onChange('ineligiblePEs', scheduleData.ineligiblePEs.filter(item => item.id !== id));
  };

  useEffect(() => {
    let totalLossesUtilisedMainTI = 0;
    let totalLossesUtilisedFPEIncome = 0;

    const updatedPriorPeriods = scheduleData.priorPeriods.map(item => {
      const lossesMainTI = safeParseFloat(item.lossesUtilisedAgainstMainTI);
      const lossesFPEIncome = safeParseFloat(item.lossesUtilisedAgainstFPEIncome);
      totalLossesUtilisedMainTI += lossesMainTI;
      totalLossesUtilisedFPEIncome += lossesFPEIncome;
      const cfLosses = Math.max(0, lossesMainTI - lossesFPEIncome).toFixed(2);
      return { ...item, cfLossesUtilisedAgainstFPEIncome: cfLosses };
    });
    
    const priorLossesFullyOffsetCheck = totalLossesUtilisedMainTI <= totalLossesUtilisedFPEIncome;

    if (JSON.stringify(updatedPriorPeriods) !== JSON.stringify(scheduleData.priorPeriods)) {
      onChange('priorPeriods', updatedPriorPeriods);
    }
    if (scheduleData.priorLossesFullyOffset !== priorLossesFullyOffsetCheck) {
        onChange('priorLossesFullyOffset', priorLossesFullyOffsetCheck);
    }

  }, [scheduleData.priorPeriods, onChange, scheduleData.priorLossesFullyOffset]); // Added scheduleData.priorLossesFullyOffset to dependency array

  useEffect(() => {
    const netIncomeCurrent = safeParseFloat(scheduleData.currentFPEligibleNetIncome);
    const netLossCurrent = safeParseFloat(scheduleData.currentFPEligibleNetLoss);
    let adjustmentAmount = 0;

    if (netIncomeCurrent > 0) {
      adjustmentAmount = netIncomeCurrent;
    } else if (netLossCurrent > 0) {
      adjustmentAmount = -netLossCurrent;
    }
    
    const adjustmentAmountStr = adjustmentAmount.toFixed(2);
    if (scheduleData.netIncomeLossForAdjustment !== adjustmentAmountStr) {
        onChange('netIncomeLossForAdjustment', adjustmentAmountStr);
    }

  }, [scheduleData.currentFPEligibleNetIncome, scheduleData.currentFPEligibleNetLoss, onChange, scheduleData.netIncomeLossForAdjustment]);

  const isExemptionValid = scheduleData.priorLossesFullyOffset !== false; 

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-sky-600">{t('sch14_title')}</h3>
      <p className="text-sm text-gray-600">{t('sch14_intro_note')}</p>

      {!isExemptionValid && (
        <div className="p-3 my-4 bg-red-100 border border-red-300 text-red-700 rounded-md">
          <p className="font-semibold">{t('sch14_exemption_not_available_title')}</p>
          <p>{t('sch14_exemption_not_available_desc')}</p>
        </div>
      )}

      <SectionHeader title={t('sch14_s1_title')} />
      {scheduleData.priorPeriods.map((item, index) => (
        <div key={item.id} className="p-3 border rounded-md space-y-2 bg-slate-50 relative">
          <p className="text-sm font-semibold text-gray-600">{t('sch14_s1_period_item_heading', {index: index + 1})}</p>
          <Input label={t('sch14_s1_tax_period_label')} type="text" id={`fpePPIdentifier-${item.id}`} value={item.taxPeriodIdentifier} onChange={(e) => handlePriorPeriodChange(index, 'taxPeriodIdentifier', e.target.value)} error={(errors?.priorPeriods as FormErrorsStep[])?.[index]?.taxPeriodIdentifier as string} placeholder="e.g. 2023-2024"/>
          <Input label={t('sch14_s1_taxable_income_label')} type="number" id={`fpePPTaxableIncome-${item.id}`} value={item.taxableIncomeFPE} onChange={(e) => handlePriorPeriodChange(index, 'taxableIncomeFPE', e.target.value)} error={(errors?.priorPeriods as FormErrorsStep[])?.[index]?.taxableIncomeFPE as string}/>
          <Input label={t('sch14_s1_tax_loss_label')} type="number" id={`fpePPTaxLoss-${item.id}`} value={item.taxLossFPE} onChange={(e) => handlePriorPeriodChange(index, 'taxLossFPE', e.target.value)} error={(errors?.priorPeriods as FormErrorsStep[])?.[index]?.taxLossFPE as string}/>
          <RadioGroup label={t('sch14_s1_election_made_label')} name={`fpePPElectionMade-${item.id}`} value={item.electionMadeInPeriod} onChange={(val) => handlePriorPeriodChange(index, 'electionMadeInPeriod', val)} options={[{label: t('yes'), value: 'yes'}, {label: t('no'), value: 'no'}]} t={t} error={(errors?.priorPeriods as FormErrorsStep[])?.[index]?.electionMadeInPeriod as string}/>
          <Input label={t('sch14_s1_losses_util_main_ti_label')} type="number" id={`fpePPLossesMainTI-${item.id}`} value={item.lossesUtilisedAgainstMainTI} onChange={(e) => handlePriorPeriodChange(index, 'lossesUtilisedAgainstMainTI', e.target.value)} error={(errors?.priorPeriods as FormErrorsStep[])?.[index]?.lossesUtilisedAgainstMainTI as string}/>
          <Input label={t('sch14_s1_losses_util_fpe_income_label')} type="number" id={`fpePPLossesFPEIncome-${item.id}`} value={item.lossesUtilisedAgainstFPEIncome} onChange={(e) => handlePriorPeriodChange(index, 'lossesUtilisedAgainstFPEIncome', e.target.value)} error={(errors?.priorPeriods as FormErrorsStep[])?.[index]?.lossesUtilisedAgainstFPEIncome as string}/>
          <Input label={t('sch14_s1_cf_losses_util_label')} type="text" id={`fpePPCFLosses-${item.id}`} value={item.cfLossesUtilisedAgainstFPEIncome || '0.00'} readOnly className="bg-gray-100"/>
          <Button onClick={() => removePriorPeriod(item.id)} variant="danger_outline" size="sm" className="absolute top-2 right-2">{t('remove_item_button')}</Button>
        </div>
      ))}
      <Button onClick={addPriorPeriod} variant="secondary" size="sm">{t('sch14_add_prior_period_button')}</Button>

      <SectionHeader title={t('sch14_s2_title')} />
      <Input label={t('sch14_s2_taxable_income_label')} type="number" id="fpeCurrentTaxableIncome" value={scheduleData.currentFPEligibleNetIncome} onChange={(e) => onChange('currentFPEligibleNetIncome', e.target.value)} error={errors?.currentFPEligibleNetIncome as string} helpText={t('sch14_s2_income_loss_help')} />
      <Input label={t('sch14_s2_tax_loss_label')} type="number" id="fpeCurrentTaxLoss" value={scheduleData.currentFPEligibleNetLoss} onChange={(e) => onChange('currentFPEligibleNetLoss', e.target.value)} error={errors?.currentFPEligibleNetLoss as string} helpText={t('sch14_s2_income_loss_help')} />
      <Input label={t('sch14_s2_net_adjustment_label')} type="text" id="fpeNetAdjustment" value={scheduleData.netIncomeLossForAdjustment || '0.00'} readOnly className="bg-gray-100 font-semibold" helpText={t('sch14_s2_net_adjustment_help')} />


      <SectionHeader title={t('sch14_s3_title')} />
      {scheduleData.ineligiblePEs.map((item, index) => (
        <div key={item.id} className="p-3 border rounded-md space-y-2 bg-slate-50 relative">
            <Input label={t('sch14_s3_jurisdiction_label')} type="text" id={`fpeIneligibleJurisdiction-${item.id}`} value={item.jurisdictionName} onChange={(e) => handleIneligiblePEChange(index, 'jurisdictionName', e.target.value)} error={(errors?.ineligiblePEs as FormErrorsStep[])?.[index]?.jurisdictionName as string}/>
            <Button onClick={() => removeIneligiblePE(item.id)} variant="danger_outline" size="sm" className="absolute top-2 right-2">{t('remove_item_button')}</Button>
        </div>
      ))}
      <Button onClick={addIneligiblePE} variant="secondary" size="sm">{t('sch14_add_ineligible_pe_button')}</Button>

    </div>
  );
};
